import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, Archive, ClipboardText, Pill, Flame, DotsSixVertical } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { InlineEdit } from '@/components/DraggableList';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const FAMILIES = ['Gravidez', 'Pós-parto', 'Introdução alimentar', 'Emagrecimento', 'Pré-conceção'];

export default function ProtocolosPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [protocols, setProtocols] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState({ name: '', family: 'Gravidez', eligibility: '', checklist: [''], nutritional_targets: { kcal: '', proteina_g: '', hidratos_g: '', gordura_g: '' }, supplementation: [{ name: '', dose: '', notes: '' }] });
  // Drag state for checklist and supplementation
  const [dragCL, setDragCL] = useState(null);
  const [overCL, setOverCL] = useState(null);
  const [dragSupp, setDragSupp] = useState(null);
  const [overSupp, setOverSupp] = useState(null);

  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/protocols?include_archived=${showArchived}`); setProtocols(data); } catch {}
  }, [showArchived]);
  useEffect(() => { fetch(); }, [fetch]);

  const resetForm = () => setForm({ name: '', family: 'Gravidez', eligibility: '', checklist: [''], nutritional_targets: { kcal: '', proteina_g: '', hidratos_g: '', gordura_g: '' }, supplementation: [{ name: '', dose: '', notes: '' }] });

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nome obrigatório'); return; }
    const payload = { ...form, checklist: form.checklist.filter(c => c.trim()), supplementation: form.supplementation.filter(s => s.name.trim()) };
    try {
      if (editId) { await axios.put(`${API}/api/protocols/${editId}`, payload); }
      else { await axios.post(`${API}/api/protocols`, payload); }
      toast.success('Protocolo guardado'); setShowForm(false); setEditId(null); resetForm(); fetch();
    } catch { toast.error('Erro'); }
  };

  const archive = async (id) => { await axios.put(`${API}/api/protocols/${id}`, { archived: true }); toast.success('Arquivado'); fetch(); };
  const unarchive = async (id) => { await axios.put(`${API}/api/protocols/${id}`, { archived: false }); toast.success('Reativado'); fetch(); };
  const del = async (id) => {
    const p = protocols.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o protocolo "${p?.name || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/protocols/${id}`); toast.success('Eliminado'); fetch();
  };

  // Inline rename
  const renameProtocol = async (id, name) => {
    try { await axios.put(`${API}/api/protocols/${id}`, { name }); fetch(); } catch { toast.error('Erro ao renomear'); }
  };

  const addChecklistItem = () => setForm({ ...form, checklist: [...form.checklist, ''] });
  const updateChecklistItem = (i, v) => { const c = [...form.checklist]; c[i] = v; setForm({ ...form, checklist: c }); };
  const removeChecklistItem = (i) => setForm({ ...form, checklist: form.checklist.filter((_, idx) => idx !== i) });
  const reorderChecklist = (srcIdx, targetIdx) => {
    const arr = [...form.checklist];
    const [moved] = arr.splice(srcIdx, 1);
    arr.splice(targetIdx > srcIdx ? targetIdx - 1 : targetIdx, 0, moved);
    setForm({ ...form, checklist: arr });
  };

  const addSupp = () => setForm({ ...form, supplementation: [...form.supplementation, { name: '', dose: '', notes: '' }] });
  const updateSupp = (i, f, v) => { const s = [...form.supplementation]; s[i] = { ...s[i], [f]: v }; setForm({ ...form, supplementation: s }); };
  const removeSupp = (i) => setForm({ ...form, supplementation: form.supplementation.filter((_, idx) => idx !== i) });
  const reorderSupp = (srcIdx, targetIdx) => {
    const arr = [...form.supplementation];
    const [moved] = arr.splice(srcIdx, 1);
    arr.splice(targetIdx > srcIdx ? targetIdx - 1 : targetIdx, 0, moved);
    setForm({ ...form, supplementation: arr });
  };

  const grouped = {};
  protocols.forEach(p => { const f = p.family || 'Outros'; if (!grouped[f]) grouped[f] = []; grouped[f].push(p); });

  return (
    <div className="fade-in" data-testid="protocolos-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Protocolos Clínicos</h2>
          <p className="text-xs text-brand-muted mt-0.5">Gestão de protocolos por programa</p>
        </div>
        <div className="flex gap-2">
          <label className="flex items-center gap-1.5 text-xs text-brand-muted cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="rounded" /> Mostrar arquivados
          </label>
          <button onClick={() => { resetForm(); setEditId(null); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-protocol-btn">
            <Plus size={16} /> Novo protocolo
          </button>
        </div>
      </div>

      {protocols.length === 0 ? (
        <div className="text-center py-16 bg-white border border-brand-border rounded-2xl">
          <ClipboardText size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted text-sm">Sem protocolos configurados.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([family, prots]) => (
            <div key={family}>
              <h3 className="font-heading font-medium text-sm text-brand-green mb-2">{family}</h3>
              <div className="space-y-2">
                {prots.map(p => (
                  <div key={p.id} className={`bg-white border border-brand-border rounded-2xl p-4 ${p.archived ? 'opacity-50' : ''}`} data-testid={`protocol-${p.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <InlineEdit value={p.name} onSave={v => renameProtocol(p.id, v)} className="text-sm font-medium text-brand-text" testId={`protocol-name-${p.id}`} />
                        {p.eligibility && <p className="text-[10px] text-brand-muted mt-0.5">{p.eligibility}</p>}
                        {p.archived && <span className="text-[8px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Arquivado</span>}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => { setEditId(p.id); setForm({ name: p.name, family: p.family, eligibility: p.eligibility || '', checklist: p.checklist?.length ? p.checklist : [''], nutritional_targets: p.nutritional_targets || { kcal: '', proteina_g: '', hidratos_g: '', gordura_g: '' }, supplementation: p.supplementation?.length ? p.supplementation : [{ name: '', dose: '', notes: '' }] }); setShowForm(true); }} className="p-1 text-brand-placeholder hover:text-brand-green"><PencilSimple size={14} /></button>
                        {p.archived ? (
                          <button onClick={() => unarchive(p.id)} className="p-1 text-brand-placeholder hover:text-brand-green" title="Reativar"><Archive size={14} /></button>
                        ) : (
                          <button onClick={() => archive(p.id)} className="p-1 text-brand-placeholder hover:text-amber-500" title="Arquivar"><Archive size={14} /></button>
                        )}
                        <button onClick={() => del(p.id)} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={14} /></button>
                      </div>
                    </div>
                    <div className="flex gap-4 flex-wrap">
                      {p.checklist?.length > 0 && <div className="flex items-center gap-1 text-[10px] text-brand-muted"><ClipboardText size={11} /> {p.checklist.length} pontos</div>}
                      {p.nutritional_targets?.kcal && <div className="flex items-center gap-1 text-[10px] text-brand-muted"><Flame size={11} /> {p.nutritional_targets.kcal} kcal</div>}
                      {p.supplementation?.length > 0 && <div className="flex items-center gap-1 text-[10px] text-brand-muted"><Pill size={11} /> {p.supplementation.filter(s => s.name).length} suplementos</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[90vh] overflow-y-auto max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar protocolo' : 'Novo protocolo'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Gravidez, 1.o trimestre" className="rounded-xl border-brand-border" data-testid="protocol-name" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Família/Grupo</Label>
              <select value={form.family} onChange={e => setForm({ ...form, family: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white">
                {FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Critérios de elegibilidade</Label><Textarea value={form.eligibility} onChange={e => setForm({ ...form, eligibility: e.target.value })} placeholder="Ex: IG 0-12 semanas" className="rounded-xl border-brand-border" rows={2} /></div>

            {/* Checklist with drag */}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Checklist clínica</Label>
              {form.checklist.map((item, i) => (
                <div key={i}
                  draggable onDragStart={e => { setDragCL(i); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => { setDragCL(null); setOverCL(null); }}
                  onDragOver={e => { e.preventDefault(); setOverCL(i); }}
                  onDrop={e => { e.preventDefault(); setOverCL(null); if (dragCL !== null && dragCL !== i) reorderChecklist(dragCL, i); setDragCL(null); }}
                  className={`flex gap-1.5 mb-1.5 items-center transition-all ${dragCL === i ? 'opacity-40' : ''} ${overCL === i && dragCL !== i ? 'ring-1 ring-brand-green/40 rounded-lg' : ''}`}>
                  <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green"><DotsSixVertical size={12} /></div>
                  <Input value={item} onChange={e => updateChecklistItem(i, e.target.value)} placeholder="Ponto a verificar..." className="flex-1 rounded-xl border-brand-border text-xs" />
                  {form.checklist.length > 1 && <button onClick={() => removeChecklistItem(i)} className="text-red-400 p-1"><Trash size={12} /></button>}
                </div>
              ))}
              <button onClick={addChecklistItem} className="text-xs text-brand-green flex items-center gap-1"><Plus size={12} /> Adicionar ponto</button>
            </div>

            {/* Nutritional targets */}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Valores de referência nutricionais</Label>
              <div className="grid grid-cols-4 gap-2">
                <div><Label className="text-[9px] text-brand-placeholder mb-0.5 block">kcal</Label><Input type="number" value={form.nutritional_targets.kcal} onChange={e => setForm({ ...form, nutritional_targets: { ...form.nutritional_targets, kcal: e.target.value } })} className="rounded-lg border-brand-border text-xs h-8" /></div>
                <div><Label className="text-[9px] text-brand-placeholder mb-0.5 block">Proteína (g)</Label><Input type="number" value={form.nutritional_targets.proteina_g} onChange={e => setForm({ ...form, nutritional_targets: { ...form.nutritional_targets, proteina_g: e.target.value } })} className="rounded-lg border-brand-border text-xs h-8" /></div>
                <div><Label className="text-[9px] text-brand-placeholder mb-0.5 block">HC (g)</Label><Input type="number" value={form.nutritional_targets.hidratos_g} onChange={e => setForm({ ...form, nutritional_targets: { ...form.nutritional_targets, hidratos_g: e.target.value } })} className="rounded-lg border-brand-border text-xs h-8" /></div>
                <div><Label className="text-[9px] text-brand-placeholder mb-0.5 block">Gordura (g)</Label><Input type="number" value={form.nutritional_targets.gordura_g} onChange={e => setForm({ ...form, nutritional_targets: { ...form.nutritional_targets, gordura_g: e.target.value } })} className="rounded-lg border-brand-border text-xs h-8" /></div>
              </div>
            </div>

            {/* Supplementation with drag */}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Suplementação recomendada</Label>
              {form.supplementation.map((s, i) => (
                <div key={i}
                  draggable onDragStart={e => { setDragSupp(i); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => { setDragSupp(null); setOverSupp(null); }}
                  onDragOver={e => { e.preventDefault(); setOverSupp(i); }}
                  onDrop={e => { e.preventDefault(); setOverSupp(null); if (dragSupp !== null && dragSupp !== i) reorderSupp(dragSupp, i); setDragSupp(null); }}
                  className={`flex gap-1.5 mb-1.5 items-center transition-all ${dragSupp === i ? 'opacity-40' : ''} ${overSupp === i && dragSupp !== i ? 'ring-1 ring-brand-green/40 rounded-lg' : ''}`}>
                  <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green"><DotsSixVertical size={12} /></div>
                  <Input value={s.name} onChange={e => updateSupp(i, 'name', e.target.value)} placeholder="Suplemento" className="flex-1 rounded-xl border-brand-border text-xs" />
                  <Input value={s.dose} onChange={e => updateSupp(i, 'dose', e.target.value)} placeholder="Dose" className="w-24 rounded-xl border-brand-border text-xs" />
                  <Input value={s.notes} onChange={e => updateSupp(i, 'notes', e.target.value)} placeholder="Obs." className="w-24 rounded-xl border-brand-border text-xs" />
                  {form.supplementation.length > 1 && <button onClick={() => removeSupp(i)} className="text-red-400 p-1"><Trash size={12} /></button>}
                </div>
              ))}
              <button onClick={addSupp} className="text-xs text-brand-green flex items-center gap-1"><Plus size={12} /> Adicionar suplemento</button>
            </div>

            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-protocol-btn">Guardar protocolo</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
